import { inject, Injectable } from '@angular/core';
import {
  Agreements,
  commodityOptions,
  Invoices,
  Items,
  ShipmentFulfillments,
  ShipmentFulfillmentsStatus,
} from '@etoh/database/core';
import { flatten, groupBy, sum } from 'lodash';
import { combineLatest, firstValueFrom, map, Observable, take } from 'rxjs';
import { convertCurrencyToUSD } from '../../currenciesConversions/pure';
import { formatNumber, formatPercent } from '../../locales/locales';
import { LinkComponent } from '../shared/components/table/cells/link/link.component';
import {
  currencyFormatter,
  dateFormatter,
  getDisplaysForVariatealOrVintages,
  getValueForOptions,
  numberFormatter,
  percentFormatter,
} from '../shared/components/table/formatter';
import {
  agreementsContractStatusOptions,
  agreementsFulfilledStatusOptions,
} from '../shared/forms/options';
import { getUom } from '../shared/forms/uom';
import { ColDefExtended } from '../shared/list.interfaces';
import { ListService } from '../shared/list.service';
import { ShipmentFullmentsService } from '../shipment-fulfillments/shipment-fullments.service';
import { StoreService } from '../store.service';
import { getFulfillmentsStatus } from './agreements.pure';
import { AgreementsService } from './agreements.service';
import { Transaction } from './transactions.interface';

@Injectable({
  providedIn: 'root',
})
export class TransactionsService {
  private shipmentFullmentsService = inject(ShipmentFullmentsService);
  private storeService = inject(StoreService);
  private listService = inject(ListService);
  private agreementsService = inject(AgreementsService);

  get transactionsData$(): Observable<Transaction[]> {
    return combineLatest([
      this.storeService.getStoreByKeys$<Items>('items'),
      this.storeService.store.agreements.data$,
      this.storeService.store.shipmentFulfillments.data$.pipe(
        map((shipmentFulfillments) => {
          return groupBy(shipmentFulfillments, 'agreementId') as Record<
            number,
            ShipmentFulfillments[]
          >;
        })
      ),
      this.storeService.store.invoices.data$.pipe(
        map((invoices) => {
          return groupBy(invoices, 'agreementId') as Record<number, Invoices[]>;
        })
      ),
    ]).pipe(
      map(
        ([
          itemsByKey,
          agreements,
          shipmentFulfillmentsByAgreementId,
          invoicesByAgreementId,
        ]) => {
          console.log('x');

          return flatten(
            agreements.map((agreement: Agreements) => {
              const correspondingItemFulfillments =
                shipmentFulfillmentsByAgreementId[agreement.id] || [];

              return agreement.costArray.map((cost) => {
                const itemId = cost.itemId;

                const itemFulfillments = correspondingItemFulfillments.filter(
                  (itemFulfillment) => itemFulfillment.itemId === itemId
                );

                const itemFulfillmentsQuantity = sum(
                  itemFulfillments.map(
                    (itemFulfillment) => itemFulfillment.quantity || 0
                  )
                );

                const getFulfillmentsQuantity = (
                  status: ShipmentFulfillmentsStatus[]
                ): string => {
                  const sumQuantity =
                    this.shipmentFullmentsService.getFulfillmentsQuantity(
                      itemFulfillments,
                      status
                    );

                  const percent = formatPercent(sumQuantity / cost.quantity);
                  return `${formatNumber(sumQuantity)} ${
                    getUom(cost.uom) || ''
                  } (${percent})`;
                };

                const invoiceItemFulfillments =
                  this.shipmentFullmentsService.getFulfillments(
                    itemFulfillments,
                    this.shipmentFullmentsService.getStatus('invoiced')
                  );

                const getBalanceToShipQuantity = (): number => {
                  const orderedQuantity = cost.quantity;

                  const shippedQuantity =
                    this.shipmentFullmentsService.getFulfillmentsQuantity(
                      itemFulfillments,
                      this.shipmentFullmentsService.getStatus('shipped')
                    );

                  return orderedQuantity - shippedQuantity;
                };

                const balanceToShipQuantity = getBalanceToShipQuantity();

                const invoicesPerItem = invoicesByAgreementId[agreement.id]
                  ?.map((invoice) => {
                    return {
                      ...invoice,
                      shipmentFulfillment: shipmentFulfillmentsByAgreementId[
                        agreement.id
                      ]?.find(
                        (shipmentFulfillment) =>
                          shipmentFulfillment.id ===
                          invoice.shipmentFulfillmentId
                      ),
                    };
                  })
                  ?.filter(
                    (invoice) => invoice.shipmentFulfillment?.itemId === itemId
                  );

                // const totalCost = agreement.totalCost ?? 0;

                /*
                const invoicesAmount = sum(
                  (invoicesByAgreementId[agreement.id] ?? []).map(
                    (invoice) => invoice.amount ?? 0
                  )
                );
                */

                const commissionAmount =
                  this.agreementsService.getCommissionFromAgreementForSpecificItems(
                    agreement,
                    cost.itemId
                  );

                const openBalanceAmount =
                  (commissionAmount ?? 0) -
                  sum(
                    (invoicesPerItem ?? []).map(
                      (invoice) => invoice.amount ?? 0
                    )
                  );

                const splitCommissions =
                  this.agreementsService.getSplitCommissionForBrokers(
                    agreement,
                    commissionAmount
                  );

                return <Transaction>{
                  cost,
                  agreementId: agreement.id,
                  ...agreement,
                  item: itemsByKey[cost.itemId],
                  id: `${agreement.id}-${cost.itemId}`,
                  itemFulfillmentsQuantity,
                  orderedQuantity: getFulfillmentsQuantity(
                    this.shipmentFullmentsService.getStatus('ordered')
                  ),
                  shippedQuantity: getFulfillmentsQuantity(
                    this.shipmentFullmentsService.getStatus('shipped')
                  ),
                  invoicedQuantity: getFulfillmentsQuantity(
                    this.shipmentFullmentsService.getStatus('invoiced')
                  ),

                  invoicedFulfillments:
                    sum(
                      invoiceItemFulfillments.map((i) => i.commissionAmount)
                    ) || undefined,

                  invoicedCurrencyFulfillments:
                    invoiceItemFulfillments?.[0]?.currency,

                  balanceToShip: `${formatNumber(balanceToShipQuantity)} ${
                    getUom(cost.uom) || ''
                  }`,
                  balanceToShipQuantity: balanceToShipQuantity,
                  balanceToShipUom: getUom(cost.uom),

                  openBalanceAmount: openBalanceAmount,

                  openBalanceCurrency: agreement.currency,
                  commissionAmount,
                  buyerBrokerCommission: splitCommissions.buyerBrokerCommission,
                  sellerBrokerCommission:
                    splitCommissions.sellerBrokerCommission,
                  shipmentFulfillments: correspondingItemFulfillments,
                };
              });
            })
          );
        }
      )
    );
  }

  getColumnsDefsDisplayed$ = async (): Promise<ColDefExtended<any>[]> => {
    const AppellationsVintagesVarietalsFromItemColumnd = [
      // TODO csv export use cellback process
      {
        field: 'item.varietals',
        valueGetter: (params: any) => {
          return (params.data?.item as Items)?.varietals?.map((v) => v.type);
        },
        valueFormatter: (params: any) => {
          return getDisplaysForVariatealOrVintages(
            params.data?.item?.varietals
          );
        },
      },
      {
        field: 'item.vintages',
        valueGetter: (params: any) => {
          return (params.data?.item as Items)?.vintages?.map((v) => v.type);
        },
        valueFormatter: (params: any) => {
          return getDisplaysForVariatealOrVintages(params.data?.item?.vintages);
        },
      },
    ];

    const AppellationTreeColumns = [
      {
        field: 'item.country',
      },
      {
        field: 'item.state',
      },
      {
        field: 'item.region',
      },
      {
        field: 'item.subRegion',
      },
      {
        field: 'item.appellations',
        valueGetter: (params: any) => {
          return (params.data?.item as Items)?.appellations?.map((v) => v.type);
        },
        valueFormatter: (params: any) => {
          return getDisplaysForVariatealOrVintages(
            params?.data?.item?.appellations
          );
        },
      },
      {
        field: 'item.subAppellation',
      },
      {
        field: 'item.county',
      },
      {
        field: 'item.district',
      },
    ];

    const currenciesConversions = await firstValueFrom(
      this.storeService.store.currenciesConversions.data$.pipe(take(1))
    );

    return [
      this.listService.transformFirstColumnToActionColumn({
        width: 166,
        filter: 'agNumberColumnFilter',
        pinned: true,
        lockPosition: 'left',
        suppressColumnsToolPanel: true,
        field: 'item.id',
      }),
      {
        field: 'agreementId',
        filter: 'agNumberColumnFilter',
        cellRenderer: LinkComponent,
        cellRendererParams: {
          dataType: 'agreements',
        },
      },
      {
        field: 'title',
      },
      {
        field: 'itemFulfillmentsQuantity',
        headerName: 'Fulfilled quantity',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return numberFormatter(params, getUom(params.data?.cost?.uom)) as any;
        },
      },
      {
        field: 'commodityType',
        valueGetter: (params: any) =>
          getValueForOptions(
            'commodityType',
            commodityOptions,
            params.data?.commodityType
          ),
      },
      {
        field: 'contractStatus',
        valueGetter: (params: any) =>
          getValueForOptions(
            'contractStatus',
            agreementsContractStatusOptions,
            params.data?.contractStatus
          ),
      },
      {
        field: 'buyerContractRef',
      },
      {
        field: 'sellerContractRef',
      },
      {
        field: 'commissionType',
      },
      {
        field: 'commissionPercentage',
        filter: 'agNumberColumnFilter',
        valueFormatter: percentFormatter,
      },
      {
        field: 'commissionFeesPerUom',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.commissionCurrency);
        },
      },
      {
        field: 'contractOfficeId',
        dataType: 'offices',
      },

      {
        field: 'openBalanceAmount',
        filter: 'agNumberColumnFilter',

        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.openBalanceCurrency);
        },
      },
      {
        field: 'balanceToShip',
      },
      {
        field: 'balanceToShipQuantity',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'balanceToShipUom',
      },
      {
        field: 'orderedQuantity',
      },
      {
        field: 'shippedQuantity',
      },
      {
        field: 'invoicedQuantity',
      },
      {
        field: 'invoicedFulfillments',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(
            params,
            params.data?.invoicedCurrencyFulfillments
          );
        },
      },
      {
        field: 'cost.cost',
        headerName: 'Cost',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.cost?.currency) as any;
        },
      },
      {
        field: 'cost.unitPrice',
        headerName: 'Unit price',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.cost?.currency) as any;
        },
      },
      {
        headerName: 'Currency',
        valueGetter: (params: any) => {
          return params.data?.cost?.currency;
        },
      },
      {
        field: 'cost.quantity',
        headerName: 'Estimated Quantity',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return numberFormatter(params, getUom(params.data?.cost?.uom)) as any;
        },
      },
      {
        field: 'cost.uom',
        headerName: 'UOM',
        valueFormatter: (params: any) => {
          return getUom(params.data?.cost?.uom);
        },
      },
      {
        field: 'buyerCompanyId',
        dataType: 'companies',
      },
      {
        field: 'buyerOfficeId',
        dataType: 'offices',
      },
      {
        field: 'buyerSpecsId',
      },
      {
        field: 'buyerUserId',
        headerName: 'Buyer Broker',
        dataType: 'users',
      },
      {
        field: 'commissionAgreement',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: any) => {
          return this.agreementsService.getCommissionFromAgreement(params.data);
        },
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.commissionCurrency);
        },
      } as any,
      {
        headerName: 'Commission Agreement ($USD)',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: any) => {
          const agreementCommission =
            this.agreementsService.getCommissionFromAgreement(params.data);

          if (!agreementCommission) {
            return;
          }

          return convertCurrencyToUSD(
            currenciesConversions,
            params.data?.createdAt,
            params.data?.currency,
            agreementCommission
          );
        },
        valueFormatter: (params: any) => {
          return currencyFormatter(params, 'USD', '1.0-0');
        },
      },
      {
        field: 'commissionAmount',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.commissionCurrency);
        },
      } as any,
      {
        headerName: 'Commission Amount ($USD)',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: any) => {
          if (!params.data.commissionAmount) {
            return;
          }

          return convertCurrencyToUSD(
            currenciesConversions,
            params.data?.createdAt,
            params.data?.currency,
            params.data.commissionAmount
          );
        },
        valueFormatter: (params: any) => {
          return currencyFormatter(params, 'USD', '1.0-0');
        },
      },
      {
        field: 'buyerBrokerCommission',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.commissionCurrency);
        },
      } as any,
      {
        field: 'sellerBrokerCommission',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.commissionCurrency);
        },
      } as any,
      {
        field: 'sellerCompanyId',
        dataType: 'companies',
      },
      {
        field: 'sellerOfficeId',
        dataType: 'offices',
      },
      {
        headerName: 'Seller Broker',
        field: 'sellerUserId',
        dataType: 'users',
      },
      {
        field: 'startDate',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
      },
      {
        field: 'endDate',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
      },
      {
        field: 'totalCost',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params: any) => {
          return currencyFormatter(params, params.data?.commissionCurrency);
        },
      },
      ...AppellationsVintagesVarietalsFromItemColumnd,
      ...AppellationTreeColumns,
      {
        field: 'item.supplierCompanyId',
        dataType: 'companies',
      },
      {
        field: 'item.generic',
      },
      {
        field: 'item.subGeneric',
      },
      {
        field: 'item.alcohol',
        filter: 'agNumberColumnFilter',
        valueFormatter: percentFormatter,
      },
      {
        field: 'item.certifications',
      },
      {
        field: 'item.harvestMethod',
      },
      {
        field: 'item.conditioning',
      },
      {
        field: 'item.qualities',
      },
      {
        field: 'fulfillmentsStatus',
        valueGetter: (params: any) => {
          const status =
            params.data?.fulfillmentsStatus ??
            getFulfillmentsStatus(params.data, params.data?.item?.id);

          return getValueForOptions(
            'agFulfillmentsStatus',
            agreementsFulfilledStatusOptions,
            status
          );
        },
      },
      {
        headerName: 'Completion',
        valueGetter: (params: any) => {
          return this.agreementsService.getCompletionFromAgreement(
            params.data,
            params.data?.item?.id
          );
        },
        valueFormatter: (params: any) => {
          if (params.value === undefined) {
            return '';
          }
          return formatPercent(params.value);
        },
      },
      {
        field: 'isMultiYearContract',
      },
      {
        field: 'createdAt',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
      },
      // ...(await this.listService.getColumnDefinitions('agreements')),
    ];
  };
}
